<script setup lang="ts">
import { AbcResponsiveTable } from "@ordbogen-npm/abc-dsk";
import { computed, ref, watch } from "vue";

import { useArticleFont } from "@/common/composables/use-article-font.composable";
import {
  buildCountTypeHeader,
  buildGenderHeader,
  casusLabels
} from "@/components/article/dictsys/inflectionSchemes/german/labels";
import InflectionSchemeCell from "@/components/article/dictsys/inflectionSchemes/InflectionSchemeCell.vue";
import InflectionSchemeTabs from "@/components/article/dictsys/inflectionSchemes/InflectionSchemeTabs.vue";
import { makeInflectionCell } from "@/helpers/dictsys-inflection-scheme/inflection-scheme.helper";
import { useSettingsStore } from "@/stores";
import type {
  DictsysInflectionSchemeContent,
  DictsysInflectionSchemeGermanAdjectiveCase,
  DictsysInflectionSchemeGermanAdjectiveCases,
  DictsysInflectionSchemeGermanAdjectiveComparisonDegrees,
  DictsysInflectionSchemeGermanAdjectiveDegree
} from "@/types/article-formats/dictsys";
import type { InflectionScheme, InflectionTable, TabLabel } from "@/types/inflection-scheme.type";

const props = defineProps<{
  inflectionScheme: DictsysInflectionSchemeGermanAdjectiveComparisonDegrees;
}>();

const emits = defineEmits<{
  showTab: [name: string];
}>();

const settingsStore = useSettingsStore();

type TabKey = keyof DictsysInflectionSchemeGermanAdjectiveDegree;

const tabs: TabLabel<TabKey>[] = [
  {
    key: "weak",
    labels: {
      danish: "Bestemt",
      latinAsComment: "",
      latin: "Bestemt"
    }
  },
  {
    key: "mixed",
    labels: {
      danish: "Ubestemt",
      latinAsComment: "",
      latin: "Ubestemt"
    }
  },
  {
    key: "strong",
    labels: {
      danish: "Uden kendeord",
      latinAsComment: "Uden artikel",
      latin: "Uden artikel"
    }
  }
];

const activeTabKey = ref<TabKey>("weak");

watch(
  () => activeTabKey.value,
  (showTabKey) => {
    emits("showTab", showTabKey);
  }
);

const makeCaseRow = (
  caseScheme: DictsysInflectionSchemeGermanAdjectiveCase
): DictsysInflectionSchemeContent[][] => [
  caseScheme.singular.masculine,
  caseScheme.singular.feminine,
  caseScheme.singular.neutral,
  caseScheme.plural
];

const makeDegreeTable = (cases: DictsysInflectionSchemeGermanAdjectiveCases): InflectionTable => {
  const level = settingsStore.activeWordClassLevel;
  const genderHeaders = ["masculine", "feminine", "neutral"] as const;
  const genderHeaderCellsTranslated = genderHeaders.map((cell) => ({
    value: buildGenderHeader(level, cell)
  }));

  const bodyRowNominative = makeCaseRow(cases.nominative);
  const bodyRowAccusative = makeCaseRow(cases.akkusative);
  const bodyRowGenitive = makeCaseRow(cases.genitive);
  const bodyRowDative = makeCaseRow(cases.dative);

  return {
    columns: [
      { value: "Kasus" },
      ...genderHeaderCellsTranslated,
      { value: buildCountTypeHeader(level, "plural") }
    ],
    rows: [
      { cells: [makeInflectionCell(casusLabels["nominative"]), ...bodyRowNominative] },
      { cells: [makeInflectionCell(casusLabels["akkusative"]), ...bodyRowAccusative] },
      { cells: [makeInflectionCell(casusLabels["genitive"]), ...bodyRowGenitive] },
      { cells: [makeInflectionCell(casusLabels["dative"]), ...bodyRowDative] }
    ]
  };
};

const schemes = computed<InflectionScheme<TabKey>[]>(() => {
  const weakTable = makeDegreeTable(props.inflectionScheme.positive.weak);
  const mixedTable = makeDegreeTable(props.inflectionScheme.positive.mixed);
  const strongTable = makeDegreeTable(props.inflectionScheme.positive.strong);

  return [
    {
      key: "weak",
      table: weakTable
    },
    {
      key: "mixed",
      table: mixedTable
    },
    {
      key: "strong",
      table: strongTable
    }
  ];
});

const { articleFontClass } = useArticleFont();
</script>

<template>
  <div>
    <InflectionSchemeTabs
      :tabs="tabs"
      :active-tab="activeTabKey"
      @show-tab="activeTabKey = $event"
    />

    <template v-for="scheme in schemes" :key="scheme.key">
      <AbcResponsiveTable
        v-if="scheme.key === activeTabKey"
        :columns="scheme.table.columns"
        :rows="scheme.table.rows"
        :column-widths="[{ xs: '95px', xl: '128px' }]"
      >
        <template #head-cell="{ cell }">
          <span :class="articleFontClass">{{ cell }}</span>
        </template>
        <template #body-cell="{ cell, isFirst }">
          <!-- First cell of each row is actually the case name -->
          <InflectionSchemeCell v-if="isFirst" :content="cell[0].inflexion" />
          <slot v-else name="inflection-cell" v-bind="{ inflections: cell }">
            <InflectionSchemeCell :content="cell" />
          </slot>
        </template>
      </AbcResponsiveTable>
    </template>
  </div>
</template>
